import { atom } from 'jotai';
import { competitionIdsToFilterByAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '@/atoms/filters/highLevel/seasons';
import { positionPercentagesToMinutes } from '@/utils/player';
import { objSnakeToCamel } from '@/utils/queries';
import { playerCyclesOrderedAtom } from '../players';
import { playerAggsMinutesPlayedWithFallbackAtom } from './playerMinsPlayed';
import { playerPositionsByPercentageAtom, rawPlayerPositionsAtom } from './playerPositions';
import { rawPlayerRadarDataAtom } from './playerRadarData';

export type PlayerAiSummaryFilterParams = {
  competitionInfos: { id: number; name: string | null; country: string | null }[];
  seasonInfos: { id: number; name: string | null }[];
};

export const playerAiSummaryFilterParamsAtom = atom(async get => {
  const playerCycles = await get(playerCyclesOrderedAtom);
  const seasonIds = get(seasonIdsToFilterByAtom);
  const competitionIds = get(competitionIdsToFilterByAtom);

  const seasonIdSet = new Set(seasonIds);
  const competitionIdSet = new Set(competitionIds);

  const cycles = playerCycles.filter(
    ({ season, competition }) =>
      (!seasonIdSet.size || seasonIdSet.has(season.seasonId)) &&
      (!competitionIdSet.size || competitionIdSet.has(competition.competitionId)),
  );

  if (!cycles.length) return null;

  const competitionInfos = [
    ...new Map(
      cycles.map(({ competition: { competitionId: id, name, areaCode } }) => [id, { id, name, country: areaCode }]),
    ).values(),
  ];

  const seasonInfos = [...new Map(cycles.map(({ season: { seasonId: id, name } }) => [id, { id, name }])).values()];

  return { competitionInfos, seasonInfos };
});

// Most played first, so positions[0] is the primaryPosition used for the percentile lookup and the
// summary's position group. The same order as the radar's default template (playerPositionsByPercentageAtom).
export const playerSummaryPositionsAtom = atom(async get =>
  (await get(playerPositionsByPercentageAtom)).map(({ position }) => position),
);

// Per-position raw minutes for the current filter context. Paired with playerSummaryPositionsAtom so
// the agent can check whether the player has enough minutes in the selected profile's position group.
export const playerSummaryPositionMinutesAtom = atom(async get => {
  const [raw, totalMinutes] = await Promise.all([
    get(rawPlayerPositionsAtom),
    get(playerAggsMinutesPlayedWithFallbackAtom),
  ]);
  const positions = raw.data?.map(objSnakeToCamel) ?? [];
  return positionPercentagesToMinutes(positions, totalMinutes);
});

export const playerSummaryAggregatesAtom = atom(async get => {
  const raw = await get(rawPlayerRadarDataAtom);
  return raw.data ?? {};
});
